import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, Search, BookOpen } from "lucide-react";
import { handleGet, handleRemove, handlePut } from "../../../controllers/controller.all";
import Pagination from "../../../components/ui/Pagination";
import SortableHeader from "../../../components/ui/SortableHeader";
import Swal from 'sweetalert2';

export default function NoteList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [matieres, setMatieres] = useState([]);
    // Pagination & Sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const itemsPerPage = 20;
    
    // Edit Modal State
    const [editModal, setEditModal] = useState({ open: false, note: null });
    const [editForm, setEditForm] = useState({ valeur: "", appreciation: "", remarque: "" });
    const [saving, setSaving] = useState(false);
    
    useEffect(() => {
        if (!token || !ecoleId) return;
        handleGet(`/matiere?ecole=${ecoleId}`, res => setMatieres(res?.matieres || []), token);
    }, [token, ecoleId]);

    const fetchNotes = useCallback(async () => {
        setLoading(true);
        if (ecoleId) {
            await handleGet(`/note/ecole/${ecoleId}?skip=0&limit=100000`, (res) => {
                if (res?.notes) {
                    setNotes(res.notes);
                }
            }, token);
        }
        setLoading(false);
    }, [ecoleId, token]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    const handleSort = (key, order) => {
        setSortKey(key);
        setSortOrder(order);
    };

    const deleteNote = (id) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "La suppression de cette note est irréversible !",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer !',
            cancelButtonText: 'Annuler'
        }).then(async (result) => {
            if (result.isConfirmed) {
                await handleRemove(`/note/${id}`, (res) => {
                    if (res?.state === 'success') {
                        Swal.fire('Supprimé !', 'La note a été supprimée.', 'success');
                        fetchNotes();
                    } else {
                        Swal.fire('Erreur', res?.message || 'Erreur lors de la suppression', 'error');
                    }
                }, token);
            }
        });
    };

    const openEdit = (note) => {
        setEditForm({
            valeur: note.valeur || "",
            appreciation: note.appreciation || "",
            remarque: note.remarque || ""
        });
        setEditModal({ open: true, note });
    };

    const saveEdit = async () => {
        if (!editModal.note) return;
        setSaving(true);
        await handlePut(`/note/${editModal.note._id}`, editForm, (res) => {
            if (res?.state === 'success') {
                Swal.fire('Succès', 'Note modifiée avec succès', 'success');
                setEditModal({ open: false, note: null });
                fetchNotes();
            } else {
                Swal.fire('Erreur', res?.message || 'Erreur de modification', 'error');
            }
        }, token);
        setSaving(false);
    };

    const getMatiereName = (id) => {
        if (!id) return "—";
        const m = matieres.find(x => x._id === id || x._id === id?._id);
        if (m) return m.libelle;
        if (typeof id === 'object' && id.libelle) return id.libelle;
        return "—";
    };

    const getUserName = (u) => {
        if (!u) return "—";
        return `${u.prenom || ""} ${u.nom || ""}`.trim() || u.numero_etudiant || "—";
    };

    const filtered = notes.filter(n => {
        if (!search) return true;
        const s = search.toLowerCase();
        const userName = getUserName(n.user).toLowerCase();
        const matiereName = getMatiereName(n.matiere).toLowerCase();
        return userName.includes(s) || matiereName.includes(s);
    });


    const filtered_filtered = filtered.filter(note => {
        if (!search) return true;
        const s = search.toLowerCase();
        // Dynamic search across string values
        return Object.values(note).some(val => 
            typeof val === 'string' && val.toLowerCase().includes(s)
        ) || (note.nom && note.nom.toLowerCase().includes(s)) ||
             (note.libelle && note.libelle.toLowerCase().includes(s)) ||
             (note.titre && note.titre.toLowerCase().includes(s)) ||
             (note.prenom && note.prenom.toLowerCase().includes(s));
    });

    const sorted_filtered = [...filtered_filtered].sort((a, b) => {
        let valA = a[sortKey] || "";
        let valB = b[sortKey] || "";
        
        // Handle nested objects commonly used for sorting like a.classe.libelle
        if (typeof valA === 'object' && valA !== null) {
            valA = valA.libelle || valA.nom || valA.prenom || "";
        }
        if (typeof valB === 'object' && valB !== null) {
            valB = valB.libelle || valB.nom || valB.prenom || "";
        }

        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    const paginated_filtered = sorted_filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (

        <div className="flex flex-col gap-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <BookOpen className="h-6 w-6 text-indigo-600" />
                        Gestion des Notes
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Gérez et consultez les notes des élèves ({filtered.length} au total)</p>
                </div>
                <Link to="/notes/batch"
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-medium transition shadow-sm">
                    <Plus className="h-5 w-5" />
                    Nouvelle saisie (Lot)
                </Link>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-4 justify-between items-center bg-gray-50/50">
                    <div className="relative w-full sm:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher (élève, matière)..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-white"
                        />
                    </div>

                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-600 whitespace-nowrap">
                        <thead className="bg-gray-50 text-gray-700 font-medium uppercase text-xs">
                            <tr>
                                <SortableHeader label="Élève" sortKey="user" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Matière" sortKey="matiere" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Note" sortKey="valeur" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Appréciation" sortKey="appreciation" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Date Devoir" sortKey="date_devoir" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                                        Chargement des notes...
                                    </td>
                                </tr>
                            ) : paginated_filtered.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                                        Aucune note trouvée.
                                    </td>
                                </tr>
                            ) : (
                                paginated_filtered.map((note) => (
                                    <tr key={note._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 font-medium text-gray-900">
                                            {getUserName(note.user)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {getMatiereName(note.matiere)}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full font-bold text-xs ${Number(note.valeur) >= 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                {note.valeur} / {note.note_max || "20"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 truncate max-w-[150px]">
                                            {note.appreciation || "—"}
                                        </td>
                                        <td className="px-6 py-4 text-gray-500">
                                            {note.date_devoir ? new Date(note.date_devoir).toLocaleDateString("fr-FR") : "—"}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => openEdit(note)} title="Modifier"
                                                    className="p-1.5 text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition">
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => deleteNote(note._id)} title="Supprimer"
                                                    className="p-1.5 text-red-600 bg-red-50 rounded hover:bg-red-100 transition">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {filtered_filtered.length > itemsPerPage && (
                    <Pagination 
                        currentPage={currentPage} 
                        totalItems={filtered_filtered.length} 
                        itemsPerPage={itemsPerPage} 
                        onPageChange={setCurrentPage} 
                    />
                )}
            </div>

            {/* Edit Modal */}
            {editModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900">Modifier la note</h3>
                        </div>
                        <div className="p-6 flex flex-col gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Note (/{editModal.note.note_max || "20"})</label>
                                <input type="number" step="0.1" max={editModal.note.note_max || "20"}
                                    value={editForm.valeur} onChange={e => setEditForm({ ...editForm, valeur: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Appréciation</label>
                                <input type="text"
                                    value={editForm.appreciation} onChange={e => setEditForm({ ...editForm, appreciation: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Remarque (Optionnel)</label>
                                <textarea rows="2"
                                    value={editForm.remarque} onChange={e => setEditForm({ ...editForm, remarque: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none" />
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
                            <button onClick={() => setEditModal({ open: false, note: null })}
                                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                                Annuler
                            </button>
                            <button onClick={saveEdit} disabled={saving}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
                                {saving ? "Enregistrement..." : "Sauvegarder"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
